/**
 * BottomSheetCoverPicker.jsx
 * Мобильная шторка выбора обложки.
 * Использует общий BottomSheet, внутри — поиск + сетка обложек.
 * Логика идентична ModalCoverPicker, но адаптирована под мобильный UX.
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Box from "@mui/joy/Box";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import Button from "@mui/joy/Button";
import Input from "@mui/joy/Input";
import Chip from "@mui/joy/Chip";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import CircularProgress from "@mui/joy/CircularProgress";
import TelegramIcon from "@mui/icons-material/Telegram";
import HeadsetMicRoundedIcon from "@mui/icons-material/HeadsetMicRounded";

import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ImageRoundedIcon from "@mui/icons-material/ImageRounded";

import BottomSheet from "./BottomSheet";
import { bookApi } from "../../api.js";
import { Grid } from "@mui/joy";


const QUICK_TAGS = [
  "все", "россия", "мир", "предметы", "детские", "свадьба, любовь"
];


// ─── Карточка обложки (компактная, для мобильного грида) ─────────────────────

function CoverCardMobile({ cover, selected, excluded, onClick }) {
  const [loaded, setLoaded] = useState(false);

  return (
    <Grid
      onClick={() => onClick(cover)}
      xs={2} sm={2}
      sx={{
        position: "relative",
        cursor: "pointer",
        aspectRatio: "3/4",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: "16px",
        p: 1.5,
        m: 0.5,
        bgcolor: selected
          ? "color-mix(in srgb, var(--joy-palette-primary-500) 8%, var(--joy-palette-background-surface))"
          : "background.surface",
        border: selected
          ? "2.5px solid var(--joy-palette-primary-400)"
          : "2.5px solid var(--joy-palette-divider)",
        transition: "all 0.18s cubic-bezier(0.4,0,0.2,1)",
        "&:hover": {
          transform: "translateY(-4px)",
          borderColor: selected
            ? "var(--joy-palette-primary-400)"
            : "color-mix(in srgb, var(--joy-palette-primary-400) 50%, transparent)",
          "& .sticker-img": {
            filter: "drop-shadow(0 8px 16px rgba(0,0,0,0.25)) drop-shadow(0 2px 4px rgba(0,0,0,0.15))",
            transform: "scale(1.05)",
          },
        },
        "&:active": {
          transform: "scale(0.97)",
        },
        ...(excluded && {
          opacity: 0.4,
          filter: "grayscale(0.6)",
        }),
      }}
    >
      {/* Стикер */}
      {cover.image_url ? (
        <Box
          component="img"
          className="sticker-img"
          src={cover.thumbnail_url || cover.image_url}
          alt={cover.title}
          onLoad={() => setLoaded(true)}
          draggable={false}
          sx={{
            width: "85%",
            height: "auto",
            maxHeight: "75%",
            objectFit: "contain",
            display: "block",
            userSelect: "none",
            opacity: loaded ? 1 : 0,
            my: 'auto',
            filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.18)) drop-shadow(0 1px 3px rgba(0,0,0,0.12))",
            transition: "filter 0.25s, transform 0.25s, opacity 0.3s",
          }}
        />
      ) : (
        <Box sx={{
          width: "100%", height: "100%",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <ImageRoundedIcon sx={{ fontSize: 32, color: "neutral.400" }} />
        </Box>
      )}

      {/* Скелетон */}
      {!loaded && cover.image_url && (
        <Box sx={{
          position: "absolute", inset: 0,
          borderRadius: "16px",
          bgcolor: "neutral.100",
          animation: "shimmer 1.4s ease infinite",
          "@keyframes shimmer": {
            "0%, 100%": { opacity: 0.6 },
            "50%": { opacity: 1 },
          },
        }} />
      )}

      {/* Название внизу */}
      {/* <Typography sx={{
        fontSize: "12px",
        fontWeight: 600,
        color: "text.secondary",
        pt: 1,
        textAlign: "center",
        lineHeight: 1.3,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        width: "100%",
      }}>
        {cover.title}
      </Typography> */}

      {/* Галочка при выборе */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 500, damping: 28 }}
            style={{
              position: "absolute", top: 8, right: 8,
              width: 24, height: 24,
              borderRadius: "50%",
              background: "var(--joy-palette-primary-500)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 2px 8px rgba(43,92,230,0.45)",
            }}
          >
            <CheckRoundedIcon sx={{ fontSize: 14, color: "white" }} />
          </motion.div>
        )}
      </AnimatePresence>
    </Grid>
  );
}


// ─── Кнопки поддержки при пустых результатах ─────────────────────────────────

export function SupportButtons() {
  const [expanded, setExpanded] = useState(false);

  return (
    <Stack alignItems="center" gap={1} sx={{ mt: 0.5 }}>
      {!expanded ? (
        <Button
          size="sm"
          variant="soft"
          color="neutral"
          startDecorator={<HeadsetMicRoundedIcon sx={{ fontSize: 16 }} />}
          onClick={() => setExpanded(true)}
          sx={{ borderRadius: "10px", fontSize: "12px" }}
        >
          Написать в поддержку
        </Button>
      ) : (
        <Stack direction="row" gap={1}>
          <Button
            size="sm"
            variant="soft"
            color="primary"
            component="a"
            href="https://t.me/your_support_bot"
            target="_blank"
            rel="noopener noreferrer"
            startDecorator={<TelegramIcon sx={{ fontSize: 16 }} />}
            sx={{ borderRadius: "10px", fontSize: "12px" }}
          >
            Telegram
          </Button>
          <Button
            size="sm"
            variant="soft"
            color="neutral"
            component="a"
            href="https://wa.me/79991234567"
            target="_blank"
            rel="noopener noreferrer"
            startDecorator={<HeadsetMicRoundedIcon sx={{ fontSize: 16 }} />}
            sx={{ borderRadius: "10px", fontSize: "12px" }}
          >
            WhatsApp
          </Button>
        </Stack>
      )}
    </Stack>
  );
}



// ─── Главный компонент ────────────────────────────────────────────────────────

export default function BottomSheetCoverPicker({ open, onClose, onConfirm, bookId, coverBgColor }) {
  const [covers, setCovers]       = useState([]);
  const [loading, setLoading]     = useState(false);
  const [search, setSearch]       = useState("");
  const [activeTag, setActiveTag] = useState("все");
  const [selected, setSelected]   = useState(null);
  const [saving, setSaving]       = useState(false);
  const [warningCover, setWarningCover] = useState(null);

  const isExcluded = (cover) => {
    if (!coverBgColor || !cover.excluded_bg_colors?.length) return false;
    return cover.excluded_bg_colors.some(
      c => c.toUpperCase() === coverBgColor.toUpperCase()
    );
  };

  const searchTimerRef = useRef(null);

  const fetchCovers = useCallback(async (query = "") => {
    setLoading(true);
    try {
      const tag = activeTag !== "все" ? activeTag : "";
      const data = await bookApi.getCoverTemplates(query || tag);
      setCovers(data);
    } catch (err) {
      console.error("Ошибка загрузки обложек:", err.message);
    } finally {
      setLoading(false);
    }
  }, [activeTag]);

  useEffect(() => {
    if (open) {
      setSelected(null);
      setSearch("");
      setActiveTag("все");
      fetchCovers();
    }
  }, [open]);

  useEffect(() => {
    if (open) fetchCovers(search);
  }, [activeTag]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => fetchCovers(val), 450);
  };

  const handleConfirm = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      if (bookId) {
        await bookApi.updateBook(bookId, { coverTemplateId: selected.id });
      }
      onConfirm(selected);
      onClose();
    } catch (err) {
      console.error("Ошибка сохранения обложки:", err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <BottomSheet open={open} onClose={onClose} title="Выбор обложки" 
        // subTitle="Вы можете написать название города или страны, также название любого предмета,  например: «лимон», «цветок» или «животные» и т.д."
      >
        {/* ── Поиск + теги — прилипают к верху шторки ── */}
        <Box
          onPointerDown={(e) => e.stopPropagation()}
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 5,
            bgcolor: "background.surface",
            px: 2, pt: 1.5, pb: 1.5,
            borderBottom: "1px solid", borderColor: "divider",
          }}
        >
          <Input
            value={search}
            onChange={handleSearchChange}
            placeholder="Поиск обложки..."
            startDecorator={<SearchRoundedIcon sx={{ fontSize: 17, color: "text.tertiary" }} />}
            size="sm"
            sx={{ borderRadius: "10px", mb: 1.25 }}
          />

          <Typography level="body-xs" sx={{ color: 'neutral.600', fontWeight: 800, lineHeight: 1.1, mb: 1.25 }}>
            Напишите название города или страны, также название любого предмета или цвета,  например: «розовый», «цветок» или «животные» и т.д.
          </Typography>

          {/* Горизонтальный скролл тегов */}
          <Box
            sx={{
              display: "flex",
              gap: 0.75,
              overflowX: "auto",
              pb: 0.25,
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
            }}
          >
            {QUICK_TAGS.map(tag => (
              <Chip
                key={tag}
                size="sm"
                variant={activeTag === tag ? "solid" : "soft"}
                color={activeTag === tag ? "primary" : "neutral"}
                onClick={() => setActiveTag(tag)}
                sx={{
                  cursor: "pointer",
                  fontSize: "11px",
                  borderRadius: "8px",
                  flexShrink: 0,
                  fontWeight: activeTag === tag ? 700 : 400,
                  WebkitTapHighlightColor: "transparent",
                }}
              >
                {tag}
              </Chip>
            ))}
          </Box>
        </Box>

        {/* ── Сетка обложек ── */}
        <Box sx={{ px: 2, pt: 2, pb: 3 }}>
          {loading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
              <CircularProgress size="sm" />
              <Typography level="body-xs" color="neutral" mt={1.5}>
                Загружаем обложки...
              </Typography>
            </Stack>
          ) : covers.length === 0 ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 6, gap: 1.5 }}>
              <ImageRoundedIcon sx={{ fontSize: 40, color: "neutral.300" }} />
              <Typography level="body-sm" color="neutral" textAlign="center">
                Ничего не найдено.<br />Попробуйте другой запрос.
              </Typography>
              <SupportButtons />
            </Stack>
          ) : (
            <Grid container columns={{ xs: 4.3, sm: 6.26 }} sx={{ justifyContent: "center" }}>
              {covers.map(cover => (
                <CoverCardMobile
                  key={cover.id}
                  cover={cover}
                  selected={selected?.id === cover.id}
                  excluded={isExcluded(cover)}
                  onClick={(cover) => {
                    if (isExcluded(cover)) {
                      setWarningCover(cover);
                    } else {
                      setSelected(cover);
                    }
                  }}
                />
              ))}
            </Grid>
          )}
        </Box>

        {/* ── Фиксированная кнопка подтверждения ── */}
        <Box
          onPointerDown={(e) => e.stopPropagation()}
          sx={{
            position: "sticky",
            bottom: 0,
            px: 2,
            py: 1.75,
            bgcolor: "background.surface",
            borderTop: "1px solid", borderColor: "divider",
            zIndex: 5,
          }}
        >
          {selected && (
            <Stack direction="row" alignItems="center" gap={1.5} mb={1.25}>
              {selected.image_url && (
                <Box
                  component="img"
                  src={selected.thumbnail_url || selected.image_url}
                  sx={{
                    width: 30, height: 40,
                    objectFit: "cover",
                    borderRadius: "5px",
                    border: "1.5px solid",
                    borderColor: "primary.200",
                    flexShrink: 0,
                  }}
                />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Typography level="body-xs" color="neutral">Выбрано:</Typography>
                <Typography level="body-sm" fontWeight={600} noWrap>{selected.title}</Typography>
              </Box>
            </Stack>
          )}

          <Button
            fullWidth
            size="md"
            variant="solid"
            color="primary"
            disabled={!selected}
            loading={saving}
            onClick={handleConfirm}
            sx={{ borderRadius: "12px", fontWeight: 700 }}
          >
            {selected ? "Применить обложку" : "Выберите обложку"}
          </Button>
        </Box>
      </BottomSheet>

      {/* Модалка предупреждения */}
      <Modal open={!!warningCover} onClose={() => setWarningCover(null)} sx={{ zIndex: 10020 }}>
        <ModalDialog sx={{ maxWidth: 400, width: '100%', p: 2.5, borderRadius: "16px", textAlign: "center" }}>
          <Typography level="title-sm" fontWeight={700} sx={{ mb: 1 }}>
            Стикер может не сочетаться
          </Typography>
          <Typography level="body-xs" color="neutral" sx={{ mb: 2 }}>
            Цвет этого стикера совпадает или близок к цвету фона обложки и может сливаться.
          </Typography>
          <Stack direction="row" gap={1}>
            <Button
              variant="outlined" color="neutral" size="sm" fullWidth
              onClick={() => setWarningCover(null)}
              sx={{ borderRadius: "10px" }}
            >
              Отменить
            </Button>
            <Button
              variant="solid" color="primary" size="sm" fullWidth
              onClick={() => {
                setSelected(warningCover);
                setWarningCover(null);
              }}
              sx={{ borderRadius: "10px", fontWeight: 700 }}
            >
              Всё равно выбрать
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </>
  );
}